import React, { useState, useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { MdDeleteOutline } from 'react-icons/md';
import useAuth from '@contexts/useAuth';
import useSnackbar from '@hooks/useSnackbar';
import ConfirmDialog from '@components/ConfirmDialog/ConfirmDialog';
import PageHeader, {
  PAGE_HEADER_VARIANT_SMALL,
} from '@components/2-molecules/PageHeader';
import { Frame, TableContainer } from '../Settings.styled';
import {
  Wrapper,
  SectionTitle,
  SectionDescription,
  MemoryTable,
  MemoryText,
  DateCell,
  DeleteButton,
  PaginationRow,
  PageInfo,
  PageButtons,
  PageButton,
  EmptyState,
  LoadingState,
  ActionCell,
} from './MemoriesSettings.styled';
import type { MemoryItem, MemoryPage } from '@api/memory';
import {
  fetchUserMemories,
  fetchOrgMemories,
  deleteUserMemory,
  deleteOrgMemory,
} from '@api/memory';

const PAGE_SIZE = 20;

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

interface MemorySectionProps {
  readonly title: string;
  readonly description: string;
  readonly fetchFn: (limit: number, offset: number) => Promise<MemoryPage>;
  readonly deleteFn: ((memoryId: string) => Promise<void>) | null;
  readonly testIdPrefix: string;
}

const MemorySection: React.FC<MemorySectionProps> = ({
  title,
  description,
  fetchFn,
  deleteFn,
  testIdPrefix,
}) => {
  const intl = useIntl();
  const { showError, showSuccess } = useSnackbar();
  const [page, setPage] = useState<MemoryPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [offset, setOffset] = useState(0);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      const data = await fetchFn(PAGE_SIZE, offset);
      setPage(data);
    } catch {
      setLoadFailed(true);
      showError(intl.formatMessage({ id: 'memories.error.load', defaultMessage: 'Failed to load memories' }));
    } finally {
      setLoading(false);
    }
  }, [fetchFn, offset, showError, intl]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = useCallback(
    async (memoryId: string) => {
      if (!deleteFn) return;
      setDeleting(true);
      try {
        await deleteFn(memoryId);
        showSuccess(intl.formatMessage({ id: 'memories.delete.success', defaultMessage: 'Memory deleted' }));
        setConfirmingId(null);
      } catch {
        showError(intl.formatMessage({ id: 'memories.error.delete', defaultMessage: 'Failed to delete memory' }));
        setDeleting(false);
        return;
      }
      try {
        const data = await fetchFn(PAGE_SIZE, offset);
        if (data.items.length === 0 && offset > 0) {
          setOffset((o) => Math.max(0, o - PAGE_SIZE));
        } else {
          setPage(data);
        }
      } catch {
        showError(intl.formatMessage({ id: 'memories.error.load', defaultMessage: 'Failed to load memories' }));
      } finally {
        setDeleting(false);
      }
    },
    [deleteFn, fetchFn, offset, showSuccess, showError, intl],
  );

  const totalPages = page ? Math.ceil(page.total / PAGE_SIZE) : 0;
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const isEmpty = !loadFailed && (!page || page.items.length === 0);

  const renderContent = () => {
    if (loading) {
      return <LoadingState>{intl.formatMessage({ id: 'memories.loading', defaultMessage: 'Loading memories…' })}</LoadingState>;
    }

    if (loadFailed) {
      return null;
    }

    if (isEmpty) {
      return (
        <EmptyState data-testid={`${testIdPrefix}-empty`}>
          {intl.formatMessage({ id: 'memories.empty', defaultMessage: 'No memories yet. The AI agent will save important facts here as it learns from your conversations.' })}
        </EmptyState>
      );
    }

    if (!page) {
      return null;
    }

    return (
      <>
        <TableContainer>
          <MemoryTable data-testid={`${testIdPrefix}-table`}>
            <thead>
              <tr>
                <th>{intl.formatMessage({ id: 'memories.table.memory', defaultMessage: 'Memory' })}</th>
                <th>{intl.formatMessage({ id: 'memories.table.date', defaultMessage: 'Date' })}</th>
                {deleteFn && <th />}
              </tr>
            </thead>
            <tbody>
              {page.items.map((item: MemoryItem) => (
                <tr key={item.id}>
                  <MemoryText>{item.text}</MemoryText>
                  <DateCell>{formatDate(item.date)}</DateCell>
                  {deleteFn && (
                    <ActionCell>
                      <DeleteButton
                        title={intl.formatMessage({ id: 'memories.delete.title', defaultMessage: 'Delete memory' })}
                        aria-label={intl.formatMessage({ id: 'memories.delete.title', defaultMessage: 'Delete memory' })}
                        onClick={() => setConfirmingId(item.id)}
                      >
                        <MdDeleteOutline />
                      </DeleteButton>
                    </ActionCell>
                  )}
                </tr>
              ))}
            </tbody>
          </MemoryTable>
        </TableContainer>

        {totalPages > 1 && (
          <PaginationRow>
            <PageInfo>
              {intl.formatMessage(
                { id: 'memories.pagination.info', defaultMessage: 'Page {current} of {total} ({count} memories)' },
                { current: currentPage, total: totalPages, count: page.total },
              )}
            </PageInfo>
            <PageButtons>
              <PageButton
                disabled={offset === 0}
                onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
              >
                {intl.formatMessage({ id: 'memories.pagination.previous', defaultMessage: 'Previous' })}
              </PageButton>
              <PageButton
                disabled={offset + PAGE_SIZE >= page.total}
                onClick={() => setOffset((o) => o + PAGE_SIZE)}
              >
                {intl.formatMessage({ id: 'memories.pagination.next', defaultMessage: 'Next' })}
              </PageButton>
            </PageButtons>
          </PaginationRow>
        )}
      </>
    );
  };

  return (
    <Frame data-testid={`${testIdPrefix}-section`}>
      <SectionTitle>{title}</SectionTitle>
      <SectionDescription>{description}</SectionDescription>
      {renderContent()}

      {deleteFn && (
        <ConfirmDialog
          show={confirmingId !== null}
          title={intl.formatMessage({ id: 'memories.delete.title', defaultMessage: 'Delete memory' })}
          confirmLabel={intl.formatMessage({ id: 'memories.delete.button', defaultMessage: 'Delete' })}
          cancelLabel={intl.formatMessage({ id: 'memories.delete.cancel', defaultMessage: 'Cancel' })}
          loading={deleting}
          onConfirm={() => {
            if (confirmingId) handleDelete(confirmingId);
          }}
          onCancel={() => setConfirmingId(null)}
        >
          <p>{intl.formatMessage({ id: 'memories.delete.confirm', defaultMessage: 'Are you sure you want to delete this memory?' })}</p>
        </ConfirmDialog>
      )}
    </Frame>
  );
};

const MemoriesSettings: React.FC = () => {
  const intl = useIntl();
  const { user } = useAuth();
  const isAdmin =
    user?.orgRole === 'AIBUILD_ADMIN' || user?.orgRole === 'ORG_ADMIN';

  return (
    <Wrapper data-testid="memories-settings">
      <PageHeader
        dataTestId="memories-settings__header"
        variant={PAGE_HEADER_VARIANT_SMALL}
        title={intl.formatMessage({ id: 'memories.title', defaultMessage: 'Memories' })}
      />

      {isAdmin && (
        <MemorySection
          title={intl.formatMessage({ id: 'memories.org.title', defaultMessage: 'Organization Memories' })}
          description={intl.formatMessage({ id: 'memories.org.description', defaultMessage: 'Shared engineering standards, constraints, and domain rules learned by the AI across all conversations in your organization.' })}
          fetchFn={fetchOrgMemories}
          deleteFn={deleteOrgMemory}
          testIdPrefix="org-memories"
        />
      )}

      <MemorySection
        title={intl.formatMessage({ id: 'memories.user.title', defaultMessage: 'Your Memories' })}
        description={intl.formatMessage({ id: 'memories.user.description', defaultMessage: 'Personal preferences, workflow habits, and working style learned by the AI from your conversations.' })}
        fetchFn={fetchUserMemories}
          deleteFn={deleteUserMemory}
        testIdPrefix="user-memories"
      />
    </Wrapper>
  );
};

export default MemoriesSettings;
